export interface QuoteLineGuardrailSettings {
  priceLimitMin: number;
  priceLimitMax: number;
}

export interface QuoteLineGuardrailInput {
  quantity: string;
  unitPrice: string;
  originalPrice: string | null;
  minimumQuantity?: string | null;
  maximumQuantity?: string | null;
  pricingType?: string | null;
}

export interface QuoteLinePriceBounds {
  min: number;
  max: number;
}

function numericEqual(left: string, right: string): boolean {
  const a = Number(left);
  const b = Number(right);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    return Math.abs(a - b) < 0.000001;
  }
  return left.trim() === right.trim();
}

export function quoteLinePriceBounds(
  originalPrice: string | null | undefined,
  settings: QuoteLineGuardrailSettings,
): QuoteLinePriceBounds | null {
  const base = Number(originalPrice);
  if (!Number.isFinite(base) || base <= 0) return null;
  return {
    min: base * settings.priceLimitMin,
    max: base * settings.priceLimitMax,
  };
}

export function restrictedQuoteLineIssues(
  line: QuoteLineGuardrailInput,
  settings: QuoteLineGuardrailSettings,
  baseline: QuoteLineGuardrailInput | null = null,
): {
  quantityError: string | null;
  priceError: string | null;
  priceWarning: string | null;
  quantityWarning: string | null;
  priceBounds: QuoteLinePriceBounds | null;
} {
  if ((line.pricingType ?? "").trim().toLowerCase() === "custom") {
    return { quantityError: null, priceError: null, priceWarning: null, quantityWarning: null, priceBounds: null };
  }
  const quantity = Number(line.quantity);
  const minimum = line.minimumQuantity == null || line.minimumQuantity === "" ? null : Number(line.minimumQuantity);
  const maximum = line.maximumQuantity == null || line.maximumQuantity === "" ? null : Number(line.maximumQuantity);
  const quantityOutOfRange = !Number.isInteger(quantity) || quantity <= 0
    || (minimum != null && Number.isFinite(minimum) && quantity < minimum)
    || (maximum != null && Number.isFinite(maximum) && quantity > maximum);
  const quantityUnchanged = baseline != null && numericEqual(line.quantity, baseline.quantity);
  let quantityError: string | null = null;
  let quantityWarning: string | null = null;
  if (quantityOutOfRange) {
    const range = minimum != null && maximum != null
      ? `${minimum} – ${maximum}`
      : minimum != null
        ? `${minimum}+`
        : maximum != null
          ? `up to ${maximum}`
          : "the retrieved range";
    const message = !Number.isInteger(quantity) || quantity <= 0
      ? "Quantity must be a whole number greater than zero."
      : `Quantity must stay within ${range}.`;
    if (baseline == null || quantityUnchanged) {
      quantityWarning = message;
    } else {
      quantityError = message;
    }
  }

  const priceBounds = quoteLinePriceBounds(line.originalPrice, settings);
  const unitPrice = Number(line.unitPrice);
  const priceOutOfRange = priceBounds != null
    && (!Number.isFinite(unitPrice) || unitPrice < priceBounds.min - 0.000001 || unitPrice > priceBounds.max + 0.000001);
  const priceUnchanged = baseline != null && numericEqual(line.unitPrice, baseline.unitPrice);
  let priceError: string | null = null;
  let priceWarning: string | null = null;
  if (priceBounds && priceOutOfRange) {
    const allowed = `Allowed price $${priceBounds.min.toFixed(2)}–$${priceBounds.max.toFixed(2)}`;
    if (baseline == null || priceUnchanged) {
      priceWarning = `${allowed}. This price was set outside your range and can be left as is.`;
    } else {
      priceError = allowed;
    }
  }

  return { quantityError, priceError, priceWarning, quantityWarning, priceBounds };
}

export function restrictedQuoteCopyBlockReason(
  lines: readonly QuoteLineGuardrailInput[],
  settings: QuoteLineGuardrailSettings,
): string | null {
  for (const line of lines) {
    if ((line.pricingType ?? "").trim().toLowerCase() === "custom") {
      return "You do not have permission to copy a custom price.";
    }
    const bounds = quoteLinePriceBounds(line.originalPrice, settings);
    const unitPrice = Number(line.unitPrice);
    if (
      bounds != null
      && (!Number.isFinite(unitPrice) || unitPrice < bounds.min - 0.000001 || unitPrice > bounds.max + 0.000001)
    ) {
      return "You do not have permission to copy this price.";
    }
  }
  return null;
}
